import { DEFAULTS } from "../model/defaults.ts";
import { sanitize } from "../model/limits.ts";
import type { PlanInputs } from "../model/types.ts";

/**
 * Scenario sharing. Only the delta from DEFAULTS is encoded, so a lightly
 * edited plan produces a short link. Nothing is sent anywhere — the whole
 * scenario lives in the URL fragment, which browsers do not transmit.
 */

type Json = Record<string, unknown>;

function diff(base: Json, next: Json): Json {
  const out: Json = {};
  for (const key of Object.keys(next)) {
    const a = base[key];
    const b = next[key];
    if (a && b && typeof a === "object" && typeof b === "object" && !Array.isArray(b)) {
      const nested = diff(a as Json, b as Json);
      if (Object.keys(nested).length) out[key] = nested;
    } else if (JSON.stringify(a) !== JSON.stringify(b)) {
      out[key] = b;
    }
  }
  return out;
}

function merge<T>(base: T, patch: Json): T {
  const out = { ...(base as object) } as Json;
  for (const key of Object.keys(patch)) {
    const a = out[key];
    const b = patch[key];
    out[key] =
      a && b && typeof a === "object" && typeof b === "object" && !Array.isArray(b)
        ? merge(a, b as Json)
        : b;
  }
  return out as T;
}

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): string {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeInputs(inputs: PlanInputs): string {
  const delta = diff(DEFAULTS as unknown as Json, inputs as unknown as Json);
  if (!Object.keys(delta).length) return "";
  return toBase64Url(JSON.stringify(delta));
}

export function decodeInputs(encoded: string): PlanInputs {
  if (!encoded) return DEFAULTS;
  try {
    const merged = merge(DEFAULTS, JSON.parse(fromBase64Url(encoded)) as Json);
    // A link is untrusted input: it may be hand-edited, or produced by an older
    // version of the model. Clamp before it ever reaches the simulation.
    return sanitize(merged).inputs;
  } catch {
    // A malformed link should show the defaults, not a broken page.
    return DEFAULTS;
  }
}

export function readFromLocation(): PlanInputs {
  if (typeof window === "undefined") return DEFAULTS;
  return decodeInputs(window.location.hash.replace(/^#p=/, ""));
}

export function writeToLocation(inputs: PlanInputs): void {
  if (typeof window === "undefined") return;
  const encoded = encodeInputs(inputs);
  const hash = encoded ? `#p=${encoded}` : "";
  history.replaceState(null, "", window.location.pathname + window.location.search + hash);
}

export function shareUrl(inputs: PlanInputs): string {
  const encoded = encodeInputs(inputs);
  const base = window.location.origin + window.location.pathname;
  return encoded ? `${base}#p=${encoded}` : base;
}
